import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type EmailAccount } from '../lib/api';
import { formatRelative } from '../lib/format';

export function AccountsPage() {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get<{ accounts: EmailAccount[] }>('/accounts'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/accounts/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['accounts'] }),
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-800">E-Mail-Konten</h1>
        {!adding && <button onClick={() => setAdding(true)} className="btn-primary">+ Konto hinzufügen</button>}
      </div>

      <p className="text-sm text-slate-600 mb-4">
        Postfächer hinzufügen. Bis zu 10 Konten pro User.
      </p>

      {adding && (
        <AddAccountForm onCancel={() => setAdding(false)} onSuccess={() => {
          setAdding(false); qc.invalidateQueries({ queryKey: ['accounts'] });
        }} />
      )}

      {isLoading ? <div className="text-slate-500">Laden …</div>
       : !data || data.accounts.length === 0 ? (
        !adding && <div className="card text-center text-slate-500">Noch kein Konto verbunden.</div>
       ) : (
        <div className="space-y-3 mt-4">
          {data.accounts.map((acc) => (
            <div key={acc.id} className="card flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <div className="font-semibold truncate">{acc.emailAddress}</div>
                  {acc.isDefault && <span className="text-xs bg-rla-100 text-rla-700 px-2 py-0.5 rounded">Standard</span>}
                </div>
                {acc.displayName && <div className="text-sm text-slate-600">{acc.displayName}</div>}
                <div className="text-xs text-slate-500 mt-1">
                  IMAP: {acc.imapHost}:{acc.imapPort} · SMTP: {acc.smtpHost}:{acc.smtpPort}
                </div>
                <div className="text-xs text-slate-400 mt-0.5">Letzter Connect: {formatRelative(acc.lastConnectAt)}</div>
                {acc.lastError && <div className="text-xs text-red-600 mt-1">⚠ {acc.lastError}</div>}
              </div>
              <div className="flex gap-2">
                <Link to={`/account/${acc.id}/inbox`} className="btn-primary text-sm">
                  📥 Posteingang
                </Link>
                <button onClick={() => { if (confirm(`Konto "${acc.emailAddress}" entfernen?`)) remove.mutate(acc.id); }} className="btn-danger text-sm">Entfernen</button>
              </div>
            </div>
          ))}
        </div>
       )}
    </div>
  );
}

function AddAccountForm({ onCancel, onSuccess }: { onCancel: () => void; onSuccess: () => void }) {
  const [emailAddress, setEmailAddress] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [imapHost, setImapHost] = useState('imap.strato.de');
  const [imapPort, setImapPort] = useState(993);
  const [smtpHost, setSmtpHost] = useState('smtp.strato.de');
  const [smtpPort, setSmtpPort] = useState(465);
  const [rememberPassword, setRememberPassword] = useState(false);
  const [err, setErr] = useState('');

  const create = useMutation({
    mutationFn: () => api.post('/accounts', {
      emailAddress, displayName: displayName || undefined,
      username: emailAddress, password,
      imapHost, imapPort, smtpHost, smtpPort,
      rememberPassword,
    }),
    onSuccess,
    onError: (e) => setErr(e instanceof ApiError ? e.message : 'Fehler'),
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); setErr(''); create.mutate(); }} className="card space-y-3 mb-4">
      <h2 className="font-semibold">Neues Konto</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><label className="label">E-Mail-Adresse</label>
          <input type="email" className="input" value={emailAddress} onChange={(e) => setEmailAddress(e.target.value)} required placeholder="info@rla-solutions.de" autoComplete="off" /></div>
        <div><label className="label">Anzeigename (optional)</label>
          <input type="text" className="input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Alex Bezsch" autoComplete="off" /></div>
        <div className="md:col-span-2"><label className="label">Postfach-Passwort</label>
          <input type="password" className="input" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="new-password" />
          <p className="text-xs text-slate-500 mt-1">Bei STRATO unter „Postfach bearbeiten" gesetzt — <strong>nicht</strong> dein STRATO-Konto-Passwort.</p>
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={rememberPassword} onChange={(e) => setRememberPassword(e.target.checked)} className="rounded border-slate-300" />
        Passwort sicher speichern (verschlüsselt)
        <span className="text-xs text-slate-400">— sonst musst du es bei jeder Sitzung neu eingeben</span>
      </label>
      <button type="button" onClick={() => setShowAdvanced(!showAdvanced)} className="text-xs text-rla-700 hover:underline">
        {showAdvanced ? '▼' : '▶'} Server-Einstellungen
      </button>
      {showAdvanced && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 bg-slate-50 p-3 rounded">
          <div><label className="label">IMAP-Server</label><input type="text" className="input" value={imapHost} onChange={(e) => setImapHost(e.target.value)} /></div>
          <div><label className="label">IMAP-Port</label><input type="number" className="input" value={imapPort} onChange={(e) => setImapPort(Number(e.target.value))} /></div>
          <div><label className="label">SMTP-Server</label><input type="text" className="input" value={smtpHost} onChange={(e) => setSmtpHost(e.target.value)} /></div>
          <div><label className="label">SMTP-Port</label><input type="number" className="input" value={smtpPort} onChange={(e) => setSmtpPort(Number(e.target.value))} /></div>
        </div>
      )}
      {err && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md p-3">{err}</div>}
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onCancel} className="btn-secondary">Abbrechen</button>
        <button type="submit" disabled={create.isPending} className="btn-primary">
          {create.isPending ? 'Teste Verbindung …' : 'Hinzufügen'}
        </button>
      </div>
    </form>
  );
}
